import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { StaticRouter } from 'react-router-dom/server';
import {
  createTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import ExcelJS from 'exceljs';
import {
  formatColumn,
  formatRowData,
  formatRowDataTotal,
} from 'configs/cell_xlsx';

const plainText = (node: React.ReactNode) => {
  const markup = renderToStaticMarkup(
    <StaticRouter location='/'>{node}</StaticRouter>,
  );
  const doc = new DOMParser().parseFromString(markup, 'text/html');
  doc.querySelectorAll('a + a').forEach((el) => el.prepend('\n'));
  doc.querySelectorAll('br').forEach((el) => el.replaceWith('\n'));
  doc.querySelectorAll('p,div,li').forEach((el) => {
    el.prepend('\n');
    el.append('\n');
  });
  return (doc.body.textContent || '').trim();
};

export type ListWorkbookOptions = {
  values?: Record<string, (row: any) => string | number>;
  totals?: Record<string, string | number>;
};

export const createListWorkbook = (
  data: any[],
  columns: ColumnDef<any>[],
  orderColumn = false,
  options: ListWorkbookOptions = {},
) => {
  const table = createTable({
    data,
    columns,
    state: {},
    onStateChange: () => {},
    renderFallbackValue: '',
    getCoreRowModel: getCoreRowModel(),
  });
  const exportColumns = table
    .getAllLeafColumns()
    .filter(
      (column) =>
        column.columnDef.header !== 'Thao tác' &&
        !['action', 'actions', 'select'].includes(column.id),
    );
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Sheet1');
  sheet.columns = [
    ...(orderColumn ? [{ header: 'STT', width: 10 }] : []),
    ...exportColumns.map((column) => ({
      header: plainText(flexRender(column.columnDef.header, {} as any)),
      width: 40,
    })),
  ];
  sheet.getRow(1).eachCell(formatColumn);
  if (options.totals) {
    sheet
      .addRow([
        ...(orderColumn ? [''] : []),
        ...exportColumns.map((column) => options.totals?.[column.id] ?? ''),
      ])
      .eachCell(formatRowDataTotal);
  }
  table.getRowModel().rows.forEach((row, index) => {
    const values = exportColumns.map((column) => {
      const cell = row
        .getAllCells()
        .find((item) => item.column.id === column.id)!;
      const raw = cell.getValue();
      if (options.values?.[column.id])
        return options.values[column.id](row.original);
      // Export attachment URLs instead of rendering image previews or download controls.
      if (/logo|image|evidenceUrls/i.test(column.id))
        return Array.isArray(raw) ? raw.join('\n') : String(raw || '');
      return column.columnDef.cell
        ? plainText(flexRender(column.columnDef.cell, cell.getContext()))
        : typeof raw === 'object' && raw !== null
          ? JSON.stringify(raw)
          : (raw ?? '');
    });
    sheet
      .addRow([...(orderColumn ? [index + 1] : []), ...values])
      .eachCell(formatRowData);
  });
  return workbook;
};
